import { buildSpokenVoiceReply } from "./spokenResponse";

const QUESTION_PREFIX = /^(?:what|where|why|how|which|who|can|could|would|is|are|do|does|tell me|explain)\b/i;
const DEICTIC_SCENE_QUESTION = /\b(?:what (?:am i|i am) looking at|what (?:do|can) i see|what(?:'s| is) this|where am i|tell me more|what does (?:this|that|it) mean)\b/i;
const NEXT_SCENE_QUESTION = /\b(?:what(?:'s| is) next|where (?:do|should) i go next|what can i do(?: next)?|what should i do(?: next)?)\b/i;
const VERIFIED_SCENE_TERMS = /\b(?:scene|screen|view|portal|moon|gold pill|aura|exocortex|brain|synapse|neuron|neural|neurodesign|flex|ascend|mogg|skill|skills|world\s*x|worldx|sector|alpha psl|clout maxx|atmos maxx|scale maxx|world[- ]class|aura engineering|ai synthesis|ai analysis|maxx experience|elite beauty)\b/i;
const SCENE_METRIC_TERMS = /\b(?:elevation|pitch|azimuth|heading|yaw|orientation|direction|track|tracking|vertices?|faces?|fps|frame(?:s| time)?|performance|metrics?)\b/i;

function clean(value) {
  return String(value || "").trim();
}

function normalizedPortal(value) {
  const portal = clean(value).toLowerCase().replace(/_/g, "-");
  if (portal === "the-vibe-energy") return "maxx";
  if (portal === "meet-joz") return "meet-joz";
  return portal || "root";
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function angularBucket(value, size) {
  const number = finiteNumber(value);
  if (number === null) return "unknown";
  return String(Math.round(number / size));
}

export function isGroundedVoiceQuestion(value) {
  const text = clean(value).toLowerCase();
  if (!text) return false;
  if (DEICTIC_SCENE_QUESTION.test(text) || NEXT_SCENE_QUESTION.test(text)) return true;
  return QUESTION_PREFIX.test(text) && (VERIFIED_SCENE_TERMS.test(text) || SCENE_METRIC_TERMS.test(text));
}

export function buildVoiceSceneSignature({
  currentPortal,
  currentMesh,
  currentMeshStage,
  context = {},
} = {}) {
  const appContext = context?.app_context && typeof context.app_context === "object"
    ? context.app_context
    : {};
  const camera = appContext.camera_view && typeof appContext.camera_view === "object"
    ? appContext.camera_view
    : {};
  const lookAt = appContext.look_at_evidence && typeof appContext.look_at_evidence === "object"
    ? appContext.look_at_evidence
    : {};
  const worldxSector = context?.worldxSector && typeof context.worldxSector === "object"
    ? context.worldxSector
    : {};
  const focus = clean(
    lookAt.primary_object_id || appContext.focused_object || currentMesh
  ).toLowerCase();
  const sector = clean(
    worldxSector.label || worldxSector.sectorLabel || worldxSector.sectorIndex
  ).toLowerCase();

  return [
    "voice-scene-v1",
    normalizedPortal(appContext.current_portal || currentPortal),
    clean(appContext.current_stage || currentMeshStage).toLowerCase() || "none",
    focus || "none",
    sector || "none",
    angularBucket(camera.heading_degrees ?? camera.yaw_degrees, 15),
    angularBucket(camera.pitch_degrees, 10),
    appContext?.device?.mobile === true ? "mobile" : "desktop",
  ].join("|");
}

export function isVoiceSceneResponseCurrent(expectedSignature, currentSignature) {
  const expected = clean(expectedSignature);
  const current = clean(currentSignature);
  return Boolean(expected && current && expected === current);
}

export function getVoiceResponseDisposition({
  requestId,
  activeRequestId,
  backendMode,
  status,
  expectedSceneSignature,
  currentSceneSignature,
} = {}) {
  if (requestId !== activeRequestId) return "superseded";
  if (
    backendMode === "joz_llm_grounded" &&
    status === "answered" &&
    !isVoiceSceneResponseCurrent(expectedSceneSignature, currentSceneSignature)
  ) return "stale_scene";
  return "apply";
}

export function normalizeGroundedVoiceAnswer(payload = {}) {
  const displayReply = clean(payload?.reply);
  if (!displayReply) return null;
  const spokenReply = buildSpokenVoiceReply(payload?.spokenReply || displayReply);
  if (!spokenReply) return null;
  const visualEvidence = Array.isArray(payload?.visualEvidence)
    ? payload.visualEvidence
    : [];

  return {
    action: null,
    target: null,
    status: "answered",
    awareness: displayReply,
    displayReply,
    spokenReply,
    visualEvidence,
    evidenceIds: visualEvidence
      .map((evidence) => clean(evidence?.artifactId))
      .filter(Boolean),
    conversationId: clean(payload?.conversationId),
    answerSource:
      clean(payload?.trace?.answerClass) ||
      clean(payload?.intentMode) ||
      "joz_llm",
  };
}
